import React, { useCallback, useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import {
  Box,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Button,
  Chip,
  TextField,
  MenuItem,
  IconButton,
  Tooltip,
  Alert,
  Card,
  CardContent,
  Divider,
  Badge,
} from "@mui/material";
import {
  Visibility as ViewIcon,
  Refresh as RefreshIcon,
  FilterList as FilterIcon,
  Clear as ClearIcon,
  Security as SecurityIcon,
} from "@mui/icons-material";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import subscriptionApi from "../../api/subscriptions";
import useAuth from "../../hooks/useAuth";
import { format } from "date-fns";
import Layout from "../../components/layout/Layout";
import useSubscriptionRefresh from "../../hooks/useSubscriptionRefresh";
import { subscriptionCurrency, subscriptionDate as formatDate, subscriptionDaysRemaining, subscriptionDuration } from "../../utils/subscriptionDisplay";

interface Subscription {
  _id: string;
  uid: string;
  userId: {
    _id: string;
    name: string;
    email: string;
    phoneNumber: string;
  };
  planId: {
    _id: string;
    title: string;
    description: string;
    price: number;
  };
  status: "active" | "paused" | "cancelled" | "pending" | "completed";
  startDate: string;
  endDate: string;
  slot: string;
  programLength: string;
  customDuration?: number;
  nextDeliveryDate?: string | null;
  planType?: string;
  drinkTier?: string;
  ordersRemaining: number;
  skipsRemaining: number;
  paymentDetails?: any;
  dietaryPreferences: string;
  allergens: string;
  deliveryDetails: {
    name: string;
    company: string;
    flatNumber: string;
    address: string;
    coordinates: {
      latitude: number;
      longitude: number;
    };
    postcode: string;
    city: string;
    state: string;
    deliveryInstructions: string[];
    additionalNote: string;
    addressType: string;
    phoneNumber: string;
  };
  total: number;
  totalDiscount: number;
  netAmount: number;
  discountCode: string;
  deliveryTime: string;
  peopleCount: number;
  portionSize?: "classic" | "light" | string;
  createdAt: string;
  updatedAt: string;
}

const statusColors: Record<
  string,
  "success" | "warning" | "error" | "default" | "info"
> = {
  active: "success",
  paused: "warning",
  cancelled: "error",
  pending: "info",
  completed: "default",
};

const statusLabels: Record<string, string> = {
  active: "Active",
  paused: "Paused",
  cancelled: "Cancelled",
  pending: "Pending",
  completed: "Completed",
};

// Helper function to format currency
const formatCurrency = (amount: number) => {
  return new Intl.NumberFormat('en-IN', {
    style: 'currency',
    currency: 'INR',
  }).format(amount);
};

const Subscriptions: React.FC = () => {
  const { admin } = useAuth();
  const isSuperAdmin = admin?.role === "super-admin";

  const [subscriptions, setSubscriptions] = useState<Subscription[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [total, setTotal] = useState(0);

  // Filters
  const [status, setStatus] = useState<string>("");
  const [portionSize, setPortionSize] = useState<string>("");
  const [startDate, setStartDate] = useState<Date | null>(null);
  const [endDate, setEndDate] = useState<Date | null>(null);
  const [searchTerm, setSearchTerm] = useState<string>("");
  const [sortField, setSortField] = useState<string>("startDate");
  const [sortOrder, setSortOrder] = useState<"asc" | "desc">("desc");
  const [planId, setPlanId] = useState("");
  const [programLength, setProgramLength] = useState("");
  const [paymentStatus, setPaymentStatus] = useState("");
  const [expiry, setExpiry] = useState("");
  const [planOptions, setPlanOptions] = useState<Array<{ _id: string; title: string }>>([]);
  const [apiReady, setApiReady] = useState<boolean | null>(null);
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const request = useRef<AbortController | null>(null);
  const { revision, refresh, lastUpdated, setLastUpdated } = useSubscriptionRefresh();
  // Stats
  const [stats, setStats] = useState({
    total: 0,
    active: 0,
    paused: 0,
    cancelled: 0,
    pending: 0,
    completed: 0,
    activeClassic: 0,
    activeLight: 0,
    pausedClassic: 0,
    pausedLight: 0,
  });

  // Debounce search to call server-side search across all subscriptions
  useEffect(() => {
    if (searchTerm.trim() === debouncedSearch) return;
    const handler = setTimeout(() => {
      setPage(0);
      setDebouncedSearch(searchTerm.trim());
    }, 400);
    return () => clearTimeout(handler);
  }, [searchTerm, debouncedSearch]);

  const fetchSubscriptions = useCallback(async () => {
    request.current?.abort();
    const controller = new AbortController();
    request.current = controller;
    setLoading(true);

    try {
      const data = await subscriptionApi.getSubscriptions({
        status,
        portionSize: portionSize || undefined,
        startDate,
        endDate,
        page: page + 1,
        limit: rowsPerPage,
        search: debouncedSearch || undefined,
        planId, programLength, paymentStatus, expiry,
        signal: controller.signal,
        sortField,
        sortOrder,
      });

      if (controller.signal.aborted) return;
      setError(null);
      setApiReady(Array.isArray(data.planOptions));
      setPlanOptions(data.planOptions || []);
      setLastUpdated(new Date());
      setSubscriptions(data.subscriptions);
      setTotal(data.pagination.total);

      // Calculate stats from total data, not just current page
      setStats({
        total: data.totalSubscriptions || 0,
        active: data.activeSubscriptions || 0,
        paused: data.pausedSubscriptions || 0,
        cancelled: data.cancelledSubscriptions || 0,
        pending: data.pendingSubscriptions || 0,
        completed: data.completedSubscriptions || 0,
        activeClassic: data.activeClassicSubscriptions || 0,
        activeLight: data.activeLightSubscriptions || 0,
        pausedClassic: data.pausedClassicSubscriptions || 0,
        pausedLight: data.pausedLightSubscriptions || 0,
      });
    } catch (err: any) {
      if (controller.signal.aborted) return;
      setError(err.response?.data?.message || "Error fetching subscriptions");
    } finally {
      if (!controller.signal.aborted) setLoading(false);
    }
  }, [page, rowsPerPage, status, portionSize, startDate, endDate, sortField, sortOrder, debouncedSearch, planId, programLength, paymentStatus, expiry, setLastUpdated]);

  useEffect(() => {
    fetchSubscriptions();
    return () => request.current?.abort();
  }, [fetchSubscriptions, revision]);

  const handleChangePage = (event: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleResetFilters = () => {
    setStatus("");
    setPortionSize("");
    setPlanId("");
    setProgramLength("");
    setPaymentStatus("");
    setExpiry("");
    setStartDate(null);
    setEndDate(null);
    setSearchTerm("");
    setDebouncedSearch("");
    setSortField("startDate");
    setSortOrder("desc");
    setPage(0);
  };

  const handleSortChange = (value: string) => {
    const [field, order] = value.split("_");
    setSortField(field);
    setSortOrder(order === "asc" ? "asc" : "desc");
    setPage(0);
  };

  // Server-side search; no client filtering
  const filteredSubscriptions = subscriptions;

  return (
    <Layout>
      <Box>
        <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 3 }}>
          <Box><Typography variant="h4" gutterBottom>
            Subscriptions
          </Typography>
          <Typography variant="caption" color="text.secondary">{total.toLocaleString()} matching subscriptions{lastUpdated ? ` · Updated ${format(lastUpdated, "HH:mm:ss")}` : ""}</Typography></Box>
          <Button
            variant="outlined"
            startIcon={<RefreshIcon />}
            onClick={refresh}
            disabled={loading}
          >
            Refresh
          </Button>
        </Box>

        {/* Stats Cards */}
        <Typography variant="overline" color="text.secondary">All subscriptions</Typography>
        <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", sm: "repeat(2, 1fr)", lg: "repeat(3, 1fr)", xl: "repeat(6, 1fr)" }, gap: 2, mb: 3 }}>
          {/* Total */}
          <Card sx={{ border: "1px solid #e2e8f0", boxShadow: "0 1px 3px rgba(0,0,0,0.05)" }}>
            <CardContent sx={{ textAlign: "center", p: 2, "&:last-child": { pb: 2 } }}>
              <Typography variant="h5" fontWeight={800} color="primary">
                {stats.total}
              </Typography>
              <Typography variant="body2" color="text.secondary" fontWeight={600}>
                Total
              </Typography>
            </CardContent>
          </Card>

          {/* Active with Classic vs Light Breakdown */}
          <Card
            sx={{
              border: "1px solid #bbf7d0",
              bgcolor: "#f0fdf4",
              boxShadow: "0 1px 3px rgba(0,0,0,0.05)",
            }}
          >
            <CardContent sx={{ p: 2, "&:last-child": { pb: 2 } }}>
              <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 1 }}>
                <Typography variant="body2" fontWeight={700} color="success.dark">
                  Active
                </Typography>
                <Typography variant="h5" fontWeight={800} color="success.main">
                  {stats.active}
                </Typography>
              </Box>
              <Box sx={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 1, mt: 0.5 }}>
                <Tooltip title="Click to filter Active Classic">
                  <Box
                    onClick={() => {
                      setStatus("active");
                      setPortionSize("classic");
                      setPage(0);
                    }}
                    sx={{
                      p: 0.75,
                      borderRadius: 1.5,
                      bgcolor: "#fff",
                      border: "1.5px solid #d8b4fe",
                      cursor: "pointer",
                      textAlign: "center",
                      "&:hover": { bgcolor: "#f3e8ff" },
                      transition: "all 0.15s",
                    }}
                  >
                    <Typography variant="caption" sx={{ color: "#7c3aed", fontWeight: 700, display: "block", fontSize: "0.7rem" }}>
                      Classic
                    </Typography>
                    <Typography variant="subtitle2" sx={{ color: "#581c87", fontWeight: 800 }}>
                      {stats.activeClassic}
                    </Typography>
                  </Box>
                </Tooltip>
                <Tooltip title="Click to filter Active Light">
                  <Box
                    onClick={() => {
                      setStatus("active");
                      setPortionSize("light");
                      setPage(0);
                    }}
                    sx={{
                      p: 0.75,
                      borderRadius: 1.5,
                      bgcolor: "#fff",
                      border: "1.5px solid #7dd3fc",
                      cursor: "pointer",
                      textAlign: "center",
                      "&:hover": { bgcolor: "#e0f2fe" },
                      transition: "all 0.15s",
                    }}
                  >
                    <Typography variant="caption" sx={{ color: "#0284c7", fontWeight: 700, display: "block", fontSize: "0.7rem" }}>
                      Light
                    </Typography>
                    <Typography variant="subtitle2" sx={{ color: "#0369a1", fontWeight: 800 }}>
                      {stats.activeLight}
                    </Typography>
                  </Box>
                </Tooltip>
              </Box>
            </CardContent>
          </Card>

          {/* Paused with Classic vs Light Breakdown */}
          <Card
            sx={{
              border: "1px solid #fde047",
              bgcolor: "#fefce8",
              boxShadow: "0 1px 3px rgba(0,0,0,0.05)",
            }}
          >
            <CardContent sx={{ p: 2, "&:last-child": { pb: 2 } }}>
              <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 1 }}>
                <Typography variant="body2" fontWeight={700} color="warning.dark">
                  Paused
                </Typography>
                <Typography variant="h5" fontWeight={800} color="warning.main">
                  {stats.paused}
                </Typography>
              </Box>
              <Box sx={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 1, mt: 0.5 }}>
                <Tooltip title="Click to filter Paused Classic">
                  <Box
                    onClick={() => {
                      setStatus("paused");
                      setPortionSize("classic");
                      setPage(0);
                    }}
                    sx={{
                      p: 0.75,
                      borderRadius: 1.5,
                      bgcolor: "#fff",
                      border: "1.5px solid #d8b4fe",
                      cursor: "pointer",
                      textAlign: "center",
                      "&:hover": { bgcolor: "#f3e8ff" },
                      transition: "all 0.15s",
                    }}
                  >
                    <Typography variant="caption" sx={{ color: "#7c3aed", fontWeight: 700, display: "block", fontSize: "0.7rem" }}>
                      Classic
                    </Typography>
                    <Typography variant="subtitle2" sx={{ color: "#581c87", fontWeight: 800 }}>
                      {stats.pausedClassic}
                    </Typography>
                  </Box>
                </Tooltip>
                <Tooltip title="Click to filter Paused Light">
                  <Box
                    onClick={() => {
                      setStatus("paused");
                      setPortionSize("light");
                      setPage(0);
                    }}
                    sx={{
                      p: 0.75,
                      borderRadius: 1.5,
                      bgcolor: "#fff",
                      border: "1.5px solid #7dd3fc",
                      cursor: "pointer",
                      textAlign: "center",
                      "&:hover": { bgcolor: "#e0f2fe" },
                      transition: "all 0.15s",
                    }}
                  >
                    <Typography variant="caption" sx={{ color: "#0284c7", fontWeight: 700, display: "block", fontSize: "0.7rem" }}>
                      Light
                    </Typography>
                    <Typography variant="subtitle2" sx={{ color: "#0369a1", fontWeight: 800 }}>
                      {stats.pausedLight}
                    </Typography>
                  </Box>
                </Tooltip>
              </Box>
            </CardContent>
          </Card>

          {/* Pending */}
          <Card sx={{ border: "1px solid #e2e8f0", boxShadow: "0 1px 3px rgba(0,0,0,0.05)" }}>
            <CardContent sx={{ textAlign: "center", p: 2, "&:last-child": { pb: 2 } }}>
              <Typography variant="h5" fontWeight={800} color="info.main">
                {stats.pending}
              </Typography>
              <Typography variant="body2" color="text.secondary" fontWeight={600}>
                Pending
              </Typography>
            </CardContent>
          </Card>

          {/* Cancelled */}
          <Card sx={{ border: "1px solid #e2e8f0", boxShadow: "0 1px 3px rgba(0,0,0,0.05)" }}>
            <CardContent sx={{ textAlign: "center", p: 2, "&:last-child": { pb: 2 } }}>
              <Typography variant="h5" fontWeight={800} color="error.main">
                {stats.cancelled}
              </Typography>
              <Typography variant="body2" color="text.secondary" fontWeight={600}>
                Cancelled
              </Typography>
            </CardContent>
          </Card>

          {/* Completed */}
          <Card sx={{ border: "1px solid #e2e8f0", boxShadow: "0 1px 3px rgba(0,0,0,0.05)" }}>
            <CardContent sx={{ textAlign: "center", p: 2, "&:last-child": { pb: 2 } }}>
              <Typography variant="h5" fontWeight={800} color="text.secondary">
                {stats.completed}
              </Typography>
              <Typography variant="body2" color="text.secondary" fontWeight={600}>
                Completed
              </Typography>
            </CardContent>
          </Card>
        </Box>

        {/* Filters */}
        <Box sx={{ py: 2, mb: 2, borderTop: "1px solid", borderBottom: "1px solid", borderColor: "divider" }}>
          <Box sx={{ display: "flex", alignItems: "center", mb: 2 }}>
            <FilterIcon sx={{ mr: 1 }} />
            <Typography variant="h6">Filters</Typography>
          </Box>
          <Box sx={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(min(100%, 180px), 1fr))", gap: 2 }}>
            <TextField
              label="Search"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              fullWidth
              size="small"
              placeholder="Search by UID, name, email, or plan"
            />
            <TextField select label="Plan" disabled={apiReady === false} value={planId} onChange={(e) => { setPlanId(e.target.value); setPage(0); }} size="small" fullWidth>
              <MenuItem value="">All plans</MenuItem>
              {planOptions.map((plan) => <MenuItem key={plan._id} value={plan._id}>{plan.title}</MenuItem>)}
            </TextField>
            <TextField select label="Duration" disabled={apiReady === false} value={programLength} onChange={(e) => { setProgramLength(e.target.value); setPage(0); }} size="small" fullWidth>
              <MenuItem value="">All durations</MenuItem>
              {["2-days", "1-week", "4-weeks", "custom"].map((value) => <MenuItem key={value} value={value}>{value === "custom" ? "Custom" : value}</MenuItem>)}
            </TextField>
            <TextField select label="Payment" disabled={apiReady === false} value={paymentStatus} onChange={(e) => { setPaymentStatus(e.target.value); setPage(0); }} size="small" fullWidth>
              <MenuItem value="">All payments</MenuItem>
              <MenuItem value="success">Successful</MenuItem><MenuItem value="pending">Pending</MenuItem><MenuItem value="failed">Failed</MenuItem><MenuItem value="missing">No payment record</MenuItem>
            </TextField>
            <TextField select label="Expiry" disabled={apiReady === false} value={expiry} onChange={(e) => { setExpiry(e.target.value); setPage(0); }} size="small" fullWidth>
              <MenuItem value="">Any expiry</MenuItem><MenuItem value="soon">Ends within 7 days</MenuItem><MenuItem value="overdue">Past end date</MenuItem>
            </TextField>
            <TextField
              select
              label="Status"
              value={status}
              onChange={(e) => {
                setStatus(e.target.value);
                setPage(0);
              }}
              fullWidth
              size="small"
            >
              <MenuItem value="">All Statuses</MenuItem>
              <MenuItem value="active">Active</MenuItem>
              <MenuItem value="paused">Paused</MenuItem>
              <MenuItem value="pending">Pending</MenuItem>
              <MenuItem value="cancelled">Cancelled</MenuItem>
              <MenuItem value="completed">Completed</MenuItem>
            </TextField>
            <TextField
              select
              label="Portion"
              value={portionSize}
              onChange={(e) => {
                setPortionSize(e.target.value);
                setPage(0);
              }}
              fullWidth
              size="small"
            >
              <MenuItem value="">All Portions</MenuItem>
              <MenuItem value="classic">🟣 Classic</MenuItem>
              <MenuItem value="light">🔵 Light</MenuItem>
            </TextField>
            <LocalizationProvider dateAdapter={AdapterDateFns}>
              <DatePicker
                label="Starts on or after"
                value={startDate}
                onChange={(date) => { setStartDate(date); setPage(0); }}
                slotProps={{ textField: { size: "small", fullWidth: true } }}
              />
            </LocalizationProvider>
            <LocalizationProvider dateAdapter={AdapterDateFns}>
              <DatePicker
                label="Starts on or before"
                value={endDate}
                onChange={(date) => { setEndDate(date); setPage(0); }}
                slotProps={{ textField: { size: "small", fullWidth: true } }}
              />
            </LocalizationProvider>
            <TextField
              select
              label="Sort"
              value={`${sortField}_${sortOrder}`}
              onChange={(e) => handleSortChange(e.target.value)}
              fullWidth
              size="small"
            >
              <MenuItem value="startDate_desc">Start Date (Newest first)</MenuItem>
              <MenuItem value="startDate_asc">Start Date (Oldest first)</MenuItem>
              <MenuItem value="endDate_desc">End Date (Newest first)</MenuItem>
              <MenuItem value="endDate_asc">End Date (Oldest first)</MenuItem>
              <MenuItem value="updatedAt_desc">Recently updated</MenuItem>
              <MenuItem value="createdAt_desc">Recently created</MenuItem>
            </TextField>
            <Box sx={{ display: "flex", alignItems: "center" }}>
              <Button
                variant="outlined"
                onClick={handleResetFilters}
                startIcon={<ClearIcon />}
                fullWidth
              >
                Clear Filters
              </Button>
            </Box>
          </Box>
        </Box>

        {/* Error Alert */}
        {apiReady === false && <Alert severity="warning" sx={{ mb: 2 }}>Subscription server update required. Advanced filters, payment status and next-delivery data are unavailable.</Alert>}
        {error && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
            {error}
          </Alert>
        )}

        {/* Subscriptions Table */}
        <TableContainer component={Paper}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>UID</TableCell>
                <TableCell>Customer</TableCell>
                <TableCell>Plan</TableCell>
                <TableCell>Status</TableCell>
                <TableCell>Orders Remaining</TableCell>
                <TableCell>Dates</TableCell>
                <TableCell>Amount</TableCell>
                <TableCell>Delivery</TableCell>
                <TableCell>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={9} align="center">
                    Loading...
                  </TableCell>
                </TableRow>
              ) : error ? (
                <TableRow>
                  <TableCell colSpan={9} align="center">
                    Error: {error}
                  </TableCell>
                </TableRow>
              ) : filteredSubscriptions.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={9} align="center">
                    No subscriptions found
                  </TableCell>
                </TableRow>
              ) : (
                filteredSubscriptions.map((subscription) => {
                  const daysRemaining = subscriptionDaysRemaining(subscription.endDate);

                  return (
                    <TableRow key={subscription._id} hover>
                      <TableCell>
                        <Typography variant="body2" fontWeight="bold">
                          {subscription.uid}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          ID: {subscription._id.slice(-8)}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" fontWeight="medium">
                          {subscription.userId?.name || "N/A"}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {subscription.userId?.email || "N/A"}
                        </Typography>
                        {subscription.userId?.phoneNumber && (
                          <Typography variant="caption" display="block" color="text.secondary">
                            {subscription.userId.phoneNumber}
                          </Typography>
                        )}
                      </TableCell>
                      <TableCell>
                        <Box sx={{ display: "flex", alignItems: "center", gap: 0.8, mb: 0.3, flexWrap: "wrap" }}>
                          <Typography variant="body2" fontWeight="medium">
                            {subscription.planId?.title || "N/A"}
                          </Typography>
                          <Chip
                            size="small"
                            label={subscription.portionSize?.toLowerCase() === "light" ? "Light" : "Classic"}
                            sx={{
                              height: 20,
                              fontSize: "0.68rem",
                              fontWeight: 700,
                              bgcolor: subscription.portionSize?.toLowerCase() === "light" ? "#e0f2fe" : "#f3e8ff",
                              color: subscription.portionSize?.toLowerCase() === "light" ? "#0369a1" : "#7c3aed",
                              border: `1px solid ${subscription.portionSize?.toLowerCase() === "light" ? "#bae6fd" : "#ddd6fe"}`,
                            }}
                          />
                        </Box>
                        <Typography variant="caption" color="text.secondary">
                          Catalogue: {subscriptionCurrency(subscription.planId?.price)}
                        </Typography>
                        {subscription.programLength && (
                          <Typography variant="caption" display="block" color="text.secondary">
                            {subscriptionDuration(subscription.programLength, subscription.customDuration)}
                          </Typography>
                        )}
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={statusLabels[subscription.status] || subscription.status}
                          color={statusColors[subscription.status] || "default"}
                          size="small"
                        />
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2">
                          {subscription.ordersRemaining ?? "Not recorded"}
                        </Typography>
                        {subscription.skipsRemaining !== undefined && (
                          <Typography variant="caption" color="text.secondary">
                            Skips: {subscription.skipsRemaining}
                          </Typography>
                        )}
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2">
                          Start: {formatDate(subscription.startDate)}
                        </Typography>
                        <Typography variant="body2">
                          End: {formatDate(subscription.endDate)}
                        </Typography>
                        {daysRemaining !== null && subscription.status === "active" && (
                          <Typography
                            variant="caption"
                            color={daysRemaining < 0 ? "error" : daysRemaining < 7 ? "warning" : "text.secondary"}
                          >
                            {daysRemaining < 0 ? `${Math.abs(daysRemaining)} days overdue` :
                              daysRemaining === 0 ? "Ends today" :
                                `${daysRemaining} days left`}
                          </Typography>
                        )}
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" fontWeight="medium">
                          {subscriptionCurrency(subscription.netAmount)}
                        </Typography>
                        <Typography variant="caption" display="block" color={subscription.paymentDetails?.status === "failed" ? "error" : "text.secondary"}>
                          {subscription.paymentDetails?.status ? `Payment: ${subscription.paymentDetails.status}` : subscription.paymentDetails ? "Payment status unavailable" : apiReady ? "No payment record" : "Payment data unavailable"}
                        </Typography>
                        {subscription.totalDiscount > 0 && (
                          <Typography variant="caption" color="success.main">
                            -{formatCurrency(subscription.totalDiscount)}
                          </Typography>
                        )}
                        {subscription.discountCode && (
                          <Typography variant="caption" display="block" color="text.secondary">
                            Code: {subscription.discountCode}
                          </Typography>
                        )}
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2">
                          {subscription.deliveryDetails?.city || "N/A"}
                        </Typography>
                        <Typography variant="caption" display="block" color="text.secondary">
                          {subscription.nextDeliveryDate ? `Next: ${formatDate(subscription.nextDeliveryDate)}` : subscription.nextDeliveryDate === null ? "No upcoming delivery" : "Next delivery unavailable"}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {subscription.deliveryTime || "N/A"}
                        </Typography>
                        {subscription.slot && (
                          <Typography variant="caption" display="block" color="text.secondary">
                            Slot: {subscription.slot}
                          </Typography>
                        )}
                      </TableCell>
                      <TableCell>
                        <Box sx={{ display: "flex", gap: 0.5 }}>
                          <Tooltip title="View Details">
                            <IconButton
                              component={Link}
                              to={`/subscriptions/${subscription._id}`}
                              size="small"
                              color="primary"
                            >
                              <ViewIcon />
                            </IconButton>
                          </Tooltip>
                          {isSuperAdmin && subscription.status === "pending" && subscription.paymentDetails && (
                            <Tooltip title="Super Admin: Verify or Activate Payment">
                              <IconButton
                                component={Link}
                                to={`/subscriptions/${subscription._id}`}
                                size="small"
                                color="warning"
                              >
                                <SecurityIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          )}
                        </Box>
                      </TableCell>
                    </TableRow>
                  );
                })
              )}
            </TableBody>
          </Table>
          <TablePagination
            rowsPerPageOptions={[5, 10, 25, 50]}
            component="div"
            count={total}
            rowsPerPage={rowsPerPage}
            page={page}
            onPageChange={handleChangePage}
            onRowsPerPageChange={handleChangeRowsPerPage}
          />
        </TableContainer>


      </Box>
    </Layout>
  );
};

export default Subscriptions;
